import { Centrifuge, Subscription } from 'centrifuge';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    setUserBorrowsMaintenanceAction,
    setUserBorrowsMaintenanceHistoryAction,
} from '@/shared/store/borrows/actions';
import { CentrifugeApi } from '@/shared/api/centrifuge';

export const useCentrifuge = () => {
    const [centrifuge, setCentrifuge] = useState<Centrifuge | null>(null);
    const [isReady, setIsReady] = useState(false);
    const subscribedChannels = useRef(new Set<string>());
    const subscriptions = useRef(new Map<string, Subscription>());
    const dispatch = useDispatch();

    useEffect(() => {
        if (!centrifuge) {
            CentrifugeApi.on('connected', function (ctx) {
                console.log('[WS] Connected over ' + ctx.transport);
                setIsReady(true);
            });

            CentrifugeApi.connect();
            setCentrifuge(CentrifugeApi);
        }

        return () => {
            CentrifugeApi.disconnect();
            setIsReady(false);
        };
    }, []);

    useEffect(() => {
        if (!isReady || !centrifuge) return;

        const centrifugeSubscriptions = centrifuge.subscriptions();
        const entries = Object.entries(centrifugeSubscriptions);

        if (subscriptions.current.size === 0 && entries.length > 0) {
            subscriptions.current = new Map(entries);
            subscribedChannels.current = new Set(entries.map(([channel]) => channel));
        }
    }, [isReady, centrifuge]);

    const subscribeToChannel = useCallback(
        (channel: string) => {
            if (!centrifuge || !isReady) {
                console.warn(`[WS] Cannot subscribe: centrifuge not ready`);
                return;
            }

            if (subscribedChannels.current.has(channel)) {
                console.log(`[WS] Already subscribed to ${channel}`);
                return;
            }

            try {
                const subscription = centrifuge.newSubscription(channel);

                subscription
                    .on('subscribed', () => {
                        subscription.history({ limit: 100 }).then(
                            (ctx: any) => {
                                dispatch(setUserBorrowsMaintenanceHistoryAction(ctx.publications));
                            },
                            (err) => {
                                console.warn(`[WS] ${channel} history error:`, err);
                            },
                        );

                        console.log(`[WS] Subscribed to ${channel}`);
                    })
                    .on('publication', (ctx: any) => {
                        dispatch(setUserBorrowsMaintenanceHistoryAction([ctx]));
                        dispatch(setUserBorrowsMaintenanceAction(ctx.data));
                    });

                subscription.subscribe();
                subscriptions.current.set(channel, subscription);
                subscribedChannels.current.add(channel);
            } catch (e) {
                const error = e as Error;
                if (error?.message && !error?.message?.includes('already exists')) {
                    console.error(`[WS] Error subscribing to ${channel}:`, error);
                }

                unsubscribeFromChannel(channel);
            }
        },
        [centrifuge, dispatch, isReady],
    );

    const unsubscribeFromChannel = useCallback((channel: string) => {
        const subscription = subscriptions.current.get(channel);
        if (subscription) {
            try {
                subscription.unsubscribe();
                subscriptions.current.delete(channel);
                subscribedChannels.current.delete(channel);
                console.log(`[WS] Unsubscribed from ${channel}`);
            } catch (error) {
                console.error(`[WS] Error unsubscribing from ${channel}:`, error);
            }
        } else {
            console.warn(`[WS] No active subscription for ${channel}`);
        }
    }, []);

    const unsubscribeFromAllChannels = useCallback(() => {
        if (!centrifuge || !isReady) {
            return;
        }

        subscriptions.current.forEach((subscription, channel) => {
            try {
                subscription.unsubscribe();
                centrifuge.removeSubscription(subscription);
            } catch (e) {
                console.warn(`[WS] Error unsubscribing from ${channel}:`, e);
            }
        });

        subscriptions.current.clear();
        subscribedChannels.current.clear();

        console.log(`[WS] Unsubscribed from all channels`);
    }, [centrifuge, isReady]);

    return {
        centrifuge,
        isReady,
        subscribeToChannel,
        subscribedChannels: subscribedChannels.current,
        unsubscribeFromChannel,
        unsubscribeFromAllChannels,
    };
};
